// [file name]: LoginPage.js (src/pages/RestaurantPOS/LoginPage.js)
import React, { useState, useEffect, useRef } from 'react'; // ✅ Added useRef
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Typography,
  Button,
  Avatar,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  Alert,
  AlertTitle,
  Paper,
  alpha,
  Container,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import StorefrontIcon from '@mui/icons-material/Storefront';
import PeopleIcon from '@mui/icons-material/People';

import logo from '../../assets/elegantelogo.png';
import Preloader from '../../theme/Preloader';
import restaurantConfig from '../../config/Organisation_Details';
import { getRedirectPathByRole } from '../../utility/roleRedirect'; // Import the role redirect


// Configure logger
const logger = {
  info: (message, data) => console.log(`[LOGIN] ${message}`, data || ''),
  error: (message, error) => console.error(`[LOGIN] ${message}`, error || ''),
  debug: (message, data) => console.debug(`[LOGIN] ${message}`, data || ''),
  warn: (message, data) => console.warn(`[LOGIN] ${message}`, data || ''),
};

// ============================================
// AUTHENTICATION SERVICE
// ============================================

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
const API_PREFIX = process.env.REACT_APP_API_PREFIX || '/pos_v1';
const FULL_API_URL = `${API_BASE_URL}${API_PREFIX}`;
logger.info('API Configuration', { API_BASE_URL, API_PREFIX, FULL_API_URL });

const api = axios.create({
  baseURL: FULL_API_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000,
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      logger.debug('Token added to request', { url: config.url });
    }
    return config;
  },
  (error) => {
    logger.error('Request interceptor error', error);
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      const refreshToken = localStorage.getItem('refresh_token');
      logger.info('Token expired, attempting refresh');
      
      if (refreshToken) {
        try {
          const response = await axios.post(
            `${FULL_API_URL}/auth/refresh`,
            { refresh_token: refreshToken }
          );
          if (response.data.access_token) {
            localStorage.setItem('access_token', response.data.access_token);
            originalRequest.headers.Authorization = `Bearer ${response.data.access_token}`;
            logger.info('Token refreshed successfully');
            return api(originalRequest);
          }
        } catch (refreshError) {
          logger.error('Token refresh failed', refreshError);
          clearAuthData();
        }
      } else {
        logger.warn('No refresh token available');
        clearAuthData();
      }
    }
    return Promise.reject(error);
  }
);

const clearAuthData = () => {
  ['access_token', 'refresh_token', 'user_data', 'user_email', 'user_role', 'user_id', 
   'user_name', 'first_name', 'staff_id', 'department', 'phone', 'gender', 'is_active'].forEach(key => {
    localStorage.removeItem(key);
  });
  logger.info('Auth data cleared');
};

const storeUserData = (user) => {
  if (user) {
    localStorage.setItem('user_data', JSON.stringify(user));
    localStorage.setItem('user_email', user.email);
    localStorage.setItem('user_role', user.role);
    localStorage.setItem('user_id', user.id.toString());
    localStorage.setItem('user_name', `${user.first_name} ${user.other_names || ''}`.trim());
    localStorage.setItem('first_name', user.first_name);
    localStorage.setItem('staff_id', user.staff_id);
    localStorage.setItem('department', user.department);
    localStorage.setItem('phone', user.phone);
    localStorage.setItem('gender', user.gender);
    localStorage.setItem('is_active', user.is_active ? 'true' : 'false');
    logger.debug('User data stored', { email: user.email, role: user.role });
  }
};

const loginUser = async (email, password) => {
  try {
    logger.info('Login attempt', { email });
    const response = await api.post('/auth/login-json', { email, password });
    logger.debug('Login response received', { status: response.status, hasUser: !!response.data.user });
    
    if (response.data) {
      const { access_token, refresh_token, user } = response.data;
      localStorage.setItem('access_token', access_token);
      if (refresh_token) localStorage.setItem('refresh_token', refresh_token);
      storeUserData(user);
      
      logger.info('Login successful', { email: user.email, role: user.role });
      return { success: true, data: response.data, user };
    }
    return { success: false, error: 'Invalid response from server' };
  } catch (error) {
    logger.error('Login failed', error);
    let errorMessage = 'Login failed. Please try again.';
    if (error.response) {
      const errorData = error.response.data;
      errorMessage = errorData.detail || errorData.message || errorData.error || errorMessage;
      if (error.response.status === 401) errorMessage = 'Invalid email or password';
      else if (error.response.status === 403) errorMessage = 'Account is deactivated or you don\'t have permission';
      else if (error.response.status === 422) errorMessage = 'Validation error. Please check your input.';
      else if (error.response.status === 500) errorMessage = 'Server error. Please try again later.';
      logger.error('Login error details', { status: error.response.status, data: error.response.data });
    } else if (error.request) {
      errorMessage = 'Network error. Please check your connection.';
    }
    return { success: false, error: errorMessage };
  }
};

const isAuthenticated = () => {
  const token = localStorage.getItem('access_token');
  const userData = localStorage.getItem('user_data');
  return !!(token && userData);
};

const getUserRole = () => localStorage.getItem('user_role') || null;

// ============================================
// LOGIN PAGE COMPONENT
// ============================================

const Central_LoginPage = () => {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [errors, setErrors] = useState({ email: '', password: '' });
  const [serverError, setServerError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(true);

  const navigate = useNavigate();
  const location = useLocation();
  
  // ✅ Add ref to prevent double navigation
  const hasRedirected = useRef(false);

  
  const restaurantPhone = restaurantConfig.phone || '+234 800 123 4567';
  const restaurantEmail = restaurantConfig.email || 'info@greenvelvet.com';
  const restaurantAddress = restaurantConfig.address || '123 Elegante Street, Lagos, Nigeria';
  const operatingHours = restaurantConfig.operatingHours || 'Mon-Sun: 8:00 AM - 10:00 PM';
  const supportEmail = restaurantConfig.supportEmail || 'support@greenvelvet.com';
  const supportPhone = restaurantConfig.supportPhone || '+234 800 765 4321';

  // Elegant Red Theme Colors
  const primaryRed = '#C62828';
  const darkRed = '#B71C1C';
  const lightRed = '#EF5350';
  const deepRed = '#8E0000';
  const redGradient = `linear-gradient(135deg, ${darkRed} 0%, ${primaryRed} 50%, ${lightRed} 100%)`;
  const whiteColor = '#FFFFFF';
  const darkText = '#1A1A1A';
  const grayText = '#666666';
  const lightBg = '#F8F9FA';

  // Check if already authenticated
  useEffect(() => {
    const checkAuth = async () => {
      // ✅ Prevent multiple redirects
      if (hasRedirected.current) return;
      
      if (isAuthenticated()) {
        logger.info('User already authenticated, redirecting');
        hasRedirected.current = true;
        const userRole = getUserRole();
        const redirectPath = getRedirectPathByRole(userRole);
        navigate(redirectPath, { replace: true });
        return;
      }
      setIsPageLoading(false);
    };
    
    checkAuth();
  }, [navigate]);

  const validate = () => {
    const newErrors = { email: '', password: '' };
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email) newErrors.email = 'Email is required';
    else if (!emailRegex.test(formData.email)) newErrors.email = 'Enter a valid email address';
    if (!formData.password) newErrors.password = 'Password is required';
    else if (formData.password.length < 6) newErrors.password = 'Password must be at least 6 characters';
    setErrors(newErrors);
    return !newErrors.email && !newErrors.password;
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: '' });
    setServerError('');
    setSuccessMessage('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    // ✅ Prevent double submission
    if (isLoading) return;

    setIsLoading(true);
    setServerError('');
    setSuccessMessage('');

    try {
      logger.info('Login attempt', { email: formData.email });
      const result = await loginUser(formData.email, formData.password);
      
      if (result.success) {
        setSuccessMessage('Login successful!');
        setServerError('');

        const user = result.user;
        const role = user?.role || 'user';

        localStorage.setItem('email', user.email);
        localStorage.setItem('role', role);
        localStorage.setItem('userId', user.id.toString());
        localStorage.setItem('userName', `${user.first_name} ${user.other_names || ''}`.trim());
        if (user.staff_id) localStorage.setItem('staffId', user.staff_id);
        if (user.department) localStorage.setItem('userDepartment', user.department);
        if (user.phone) localStorage.setItem('phoneNumber', user.phone);

        const redirectPath = getRedirectPathByRole(role);
        logger.info('Redirecting to', { role, path: redirectPath });
        
        // ✅ Use replace to prevent going back to login
        // ✅ Small delay to ensure state updates are complete
        setTimeout(() => {
          navigate(redirectPath, { replace: true });
        }, 300);
        
      } else {
        setServerError(result.error || 'Login failed. Please try again.');
        setIsLoading(false);
      }
    } catch (error) {
      logger.error('Login error', error);
      setServerError('An unexpected error occurred. Please try again.');
      setIsLoading(false);
    }
  };

  const handleClickShowPassword = () => setShowPassword(!showPassword);

  if (isPageLoading) {
    return <Preloader text={`Loading Elegante...`} size={70} fullScreen={true} />;
  }

  if (isLoading) {
    return <Preloader text="Signing you in..." size={70} fullScreen={true} />;
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: `linear-gradient(135deg, ${alpha(primaryRed, 0.03)} 0%, ${alpha(lightRed, 0.01)} 100%)`,
        padding: 0,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Decorative Background Elements */}
      <Box sx={{ position: 'absolute', width: '100%', height: '100%', top: 0, left: 0, overflow: 'hidden', pointerEvents: 'none' }}>
        <Box sx={{
          position: 'absolute',
          top: '-30%',
          right: '-10%',
          width: '60%',
          height: '60%',
          borderRadius: '50%',
          background: `radial-gradient(circle, ${alpha(primaryRed, 0.06)} 0%, transparent 70%)`,
          animation: 'floatRed 20s ease-in-out infinite',
        }} />
        <Box sx={{
          position: 'absolute',
          bottom: '-20%',
          left: '-10%',
          width: '50%',
          height: '50%',
          borderRadius: '50%',
          background: `radial-gradient(circle, ${alpha(lightRed, 0.04)} 0%, transparent 70%)`,
          animation: 'floatRed 25s ease-in-out infinite reverse',
        }} />
        <Box sx={{
          position: 'absolute',
          top: '30%',
          left: '20%',
          width: '30%',
          height: '30%',
          borderRadius: '50%',
          background: `radial-gradient(circle, ${alpha(deepRed, 0.03)} 0%, transparent 70%)`,
          animation: 'floatRed 15s ease-in-out infinite 2s',
        }} />
        <Box sx={{
          position: 'absolute',
          bottom: '20%',
          right: '15%',
          width: '25%',
          height: '25%',
          borderRadius: '50%',
          background: `radial-gradient(circle, ${alpha(darkRed, 0.03)} 0%, transparent 70%)`,
          animation: 'floatRed 18s ease-in-out infinite 1s',
        }} />
        
        <style>
          {`
            @keyframes floatRed {
              0%, 100% { transform: translate(0, 0) scale(1); }
              25% { transform: translate(40px, -40px) scale(1.1); }
              50% { transform: translate(-30px, -60px) scale(1); }
              75% { transform: translate(30px, -30px) scale(1.05); }
            }
          `}
        </style>
      </Box>

      <Container 
        maxWidth="lg" 
        sx={{ 
          height: '100vh',
          display: 'flex', 
          alignItems: 'center',
          padding: 0,
        }}
      >
        <Grid 
          container 
          spacing={4} 
          sx={{ 
            height: '100vh',
            alignItems: 'stretch',
            padding: 0,
          }}
        >
          {/* LEFT SIDE - Brand & Info Section - Full Height */}
          <Grid item xs={12} md={6.5} sx={{ display: 'flex', height: '100vh' }}>
            <Paper
              elevation={0}
              sx={{
                width: '100%',
                p: { xs: 3, md: 5 },
                borderRadius: 0,
                background: `linear-gradient(135deg, ${whiteColor} 0%, ${alpha(whiteColor, 0.9)} 100%)`,
                border: `1px solid ${alpha(primaryRed, 0.08)}`,
                boxShadow: '0 20px 60px rgba(0,0,0,0.04), 0 8px 20px rgba(0,0,0,0.02)',
                position: 'relative',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                height: '100vh',
              }}
            >
              {/* Red accent bar */}
              <Box sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '6px',
                height: '100%',
                background: redGradient,
                borderRadius: '0 3px 3px 0',
              }} />

              {/* Logo - Made Bigger */}
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 4 }}>
                <Box
                  component="img"
                  src={logo}
                  sx={{
                    width: { xs: 120, md: 150 },
                    height: 'auto',
                    display: 'block',
                  }}
                />
              </Box>

              <Typography
                variant="body2"
                sx={{
                  color: grayText,
                  fontSize: '0.9rem',
                  textAlign: 'center',
                  mb: 3,
                }}
              >
                Sales Management System
              </Typography>

              {/* Contact Info - Centered */}
              <Box sx={{
                pt: 2,
                borderTop: `1px solid ${alpha(primaryRed, 0.06)}`,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 0.5,
                mt: 'auto',
              }}>
                <Typography variant="caption" sx={{ color: grayText, fontSize: '0.7rem', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <StorefrontIcon sx={{ fontSize: 14, color: primaryRed }} />
                  {restaurantAddress}
                </Typography>
                <Typography variant="caption" sx={{ color: grayText, fontSize: '0.7rem', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <RestaurantIcon sx={{ fontSize: 14, color: primaryRed }} />
                  {restaurantPhone} | {restaurantEmail}
                </Typography>
                <Typography variant="caption" sx={{ color: grayText, fontSize: '0.7rem', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PeopleIcon sx={{ fontSize: 14, color: primaryRed }} />
                  {operatingHours}
                </Typography>
              </Box>
            </Paper>
          </Grid>

          {/* RIGHT SIDE - Login Form - Full Height */}
          <Grid item xs={12} md={3.5} sx={{ display: 'flex', height: '100vh' }}>
            <Card
              sx={{
                width: '100%',
                p: { xs: 3, sm: 3.5 },
                borderRadius: 0,
                background: `linear-gradient(135deg, ${whiteColor} 0%, ${alpha(whiteColor, 0.95)} 100%)`,
                border: `1px solid ${alpha(primaryRed, 0.08)}`,
                boxShadow: '0 20px 60px rgba(0,0,0,0.06), 0 8px 20px rgba(0,0,0,0.03)',
                position: 'relative',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                height: '100vh',
              }}
            >
              {/* Decorative top red bar */}
              <Box sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: 4,
                background: redGradient,
              }} />

              <CardContent sx={{ p: 0, pt: 1, width: '100%' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
                  <Avatar
                    sx={{
                      width: 56,
                      height: 56,
                      background: redGradient,
                      boxShadow: `0 8px 32px ${alpha(primaryRed, 0.25)}`,
                      mb: 1.5,
                    }}
                  >
                    <LockOutlinedIcon sx={{ fontSize: 26, color: whiteColor }} />
                  </Avatar>

                  <Typography
                    variant="h5"
                    gutterBottom
                    sx={{
                      fontWeight: 700,
                      color: darkText,
                      fontSize: '1.5rem',
                      textAlign: 'center',
                    }}
                  >
                    Sign In
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      color: grayText,
                      mb: 2.5,
                      textAlign: 'center',
                      fontSize: '0.85rem',
                    }}
                  >
                    Enter your credentials to access your account
                  </Typography>

                  {/* Error/Success Messages */}
                  {serverError && (
                    <Alert severity="error" sx={{ mb: 2, width: '100%', borderRadius: 2 }}>
                      <AlertTitle>Login Failed</AlertTitle>
                      {serverError}
                    </Alert>
                  )}
                  {successMessage && (
                    <Alert severity="success" sx={{ mb: 2, width: '100%', borderRadius: 2 }}>
                      <AlertTitle>Success</AlertTitle>
                      {successMessage}
                    </Alert>
                  )}

                  <Box component="form" noValidate onSubmit={handleSubmit} sx={{ width: '100%' }}>
                    <TextField
                      placeholder="Email Address"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      variant="outlined"
                      margin="normal"
                      fullWidth
                      type="email"
                      autoComplete="email"
                      error={!!errors.email}
                      helperText={errors.email}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailOutlinedIcon sx={{ color: alpha(primaryRed, 0.4), fontSize: 20 }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          backgroundColor: alpha(lightBg, 0.8),
                          transition: 'all 0.3s ease',
                          '& fieldset': {
                            borderColor: alpha(primaryRed, 0.08),
                          },
                          '&:hover': {
                            backgroundColor: alpha(lightBg, 1),
                            '& fieldset': {
                              borderColor: alpha(primaryRed, 0.2),
                            },
                          },
                          '&.Mui-focused': {
                            backgroundColor: whiteColor,
                            '& fieldset': {
                              borderColor: primaryRed,
                              borderWidth: 2,
                            },
                          },
                          '& input': {
                            padding: '12px 8px',
                            fontSize: '0.9rem',
                          },
                        },
                        '& .MuiInputLabel-root': { display: 'none' },
                        '& .MuiFormHelperText-root': { marginLeft: '8px', fontSize: '0.7rem' },
                      }}
                    />

                    <TextField
                      placeholder="Password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      variant="outlined"
                      margin="normal"
                      fullWidth
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      error={!!errors.password}
                      helperText={errors.password}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LockOutlinedIcon sx={{ color: alpha(primaryRed, 0.4), fontSize: 20 }} />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={handleClickShowPassword} edge="end" sx={{ color: alpha(primaryRed, 0.4) }}>
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          backgroundColor: alpha(lightBg, 0.8),
                          transition: 'all 0.3s ease',
                          '& fieldset': {
                            borderColor: alpha(primaryRed, 0.08),
                          },
                          '&:hover': {
                            backgroundColor: alpha(lightBg, 1),
                            '& fieldset': {
                              borderColor: alpha(primaryRed, 0.2),
                            },
                          },
                          '&.Mui-focused': {
                            backgroundColor: whiteColor,
                            '& fieldset': {
                              borderColor: primaryRed,
                              borderWidth: 2,
                            },
                          },
                          '& input': {
                            padding: '12px 8px',
                            fontSize: '0.9rem',
                          },
                        },
                        '& .MuiInputLabel-root': { display: 'none' },
                        '& .MuiFormHelperText-root': { marginLeft: '8px', fontSize: '0.7rem' },
                      }}
                    />

                    <Button
                      type="submit"
                      variant="contained"
                      fullWidth
                      size="large"
                      disabled={isLoading}
                      sx={{
                        mt: 2.5,
                        py: 1.5,
                        textTransform: 'none',
                        fontWeight: 700,
                        fontSize: '0.95rem',
                        borderRadius: 2,
                        background: redGradient,
                        color: whiteColor,
                        position: 'relative',
                        overflow: 'hidden',
                        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        boxShadow: `0 4px 30px ${alpha(primaryRed, 0.3)}`,
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: '-100%',
                          width: '100%',
                          height: '100%',
                          background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent)',
                          transition: 'all 0.6s ease',
                        },
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: `0 8px 40px ${alpha(primaryRed, 0.4)}`,
                          '&::before': { left: '100%' },
                        },
                        '&:active': {
                          transform: 'translateY(0) scale(0.98)',
                        },
                        '&.Mui-disabled': {
                          background: alpha(primaryRed, 0.4),
                          color: alpha(whiteColor, 0.6),
                        },
                      }}
                      endIcon={!isLoading && <KeyboardArrowRightIcon />}
                    >
                      {isLoading ? 'Signing in...' : 'Sign In'}
                    </Button>

                    <Divider
                      sx={{
                        my: 2.5,
                        borderColor: alpha(primaryRed, 0.06),
                        '&::before, &::after': {
                          borderColor: alpha(primaryRed, 0.06),
                        },
                      }}
                    >
                    </Divider>

                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="body2" sx={{ color: grayText, fontSize: '0.65rem', opacity: 0.6 }}>
                        {supportPhone} | {supportEmail}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Central_LoginPage;